import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SublineApi from "../../services/SublineApi";
import PlantApi from "../../services/PlantApi";
import AreaApi from "../../services/AreaApi";
import LineApi from "../../services/LineApi";
import { useToast } from "../../context/ToastContext";
import IApiSublinesResponse from "../../interfaces/IApiSublinesResponse";
import IApiAreasResponse from "../../interfaces/IApiAreasResponse";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const EditFormSubline: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("messages");
  const { t: translateGeneral } = useTranslation("general");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [plantId, setPlantId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [lineId, setLineId] = useState("");
  
  const [plants, setPlants] = useState<{ label: string; value: string }[]>([]);
  const [areas, setAreas] = useState<{ label: string; value: string }[]>([]);
  const [lines, setLines] = useState<{ label: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  useEffect(() => {
    if (plantId) {
      fetchAreas(plantId);
    } else {
      setAreas([]);
    }
  }, [plantId]);

  useEffect(() => {
    if (areaId) {
      fetchLines(areaId);
    } else {
      setLines([]);
    }
  }, [areaId]);

  const fetchInitialData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      // 1. Fetch Plants
      const pResp = await PlantApi.GetAllPlants();
      const pOptions = (pResp.data || []).map((p: any) => ({
        label: p.name,
        value: p.id,
      }));
      setPlants(pOptions.sort((a: any, b: any) => a.label.localeCompare(b.label)));

      // 2. Fetch Subline
      const slResp = await SublineApi.GetAllSublines();
      const subline = (slResp.data || []).find((s: IApiSublinesResponse) => s.id === id);

      if (subline) {
        setName(subline.name);
        setDescription(subline.description);
        setLineId(subline.lineId);

        // 3. Fetch Line to get AreaId
        const lineResp = await LineApi.GetLineById(subline.lineId);
        const lineData = lineResp.data;
        if (lineData) {
          setAreaId(lineData.areaId);

          // 4. Fetch Area to get PlantId
          const areasResp = await AreaApi.GetAllAreas();
          const area = (areasResp.data || []).find((a: IApiAreasResponse) => a.id === lineData.areaId);
          if (area && area.productionUnit) {
            setPlantId(area.productionUnit.plantId);
          }
        }
      } else {
        displayToast("error", translateForm("error"), translateGeneral("noRecordsFound"), false, 3000);
        navigate("/sublines");
      }
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAreas = async (pId: string): Promise<void> => {
    try {
      const response = await AreaApi.GetAreaByPlantIdStatic(pId);
      const options = (response.data || []).map((a: any) => ({
        label: a.name,
        value: a.id,
      }));
      setAreas(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Erro ao buscar Ã¡reas: ", error);
    }
  };

  const fetchLines = async (aId: string): Promise<void> => {
    try {
      const response = await LineApi.GetLinesByArea(aId);
      const options = (response.data || []).map((l: any) => ({
        label: l.name,
        value: l.id,
      }));
      setLines(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Erro ao buscar linhas: ", error);
    }
  };

  const handleUpdate = async (): Promise<void> => {
    if (!name || !lineId || !id) {
      displayToast("error", translateForm("error"), translateMessages("fillAllFields"), false, 3000);
      return;
    }

    setIsLoading(true);
    try {
      await SublineApi.UpdateSubline(id, lineId, description, name);
      displayToast("success", translateMessages("editSuccess"), "", false, 3000);
      navigate("/sublines");
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard
      title={translateMessages("editSubline")}
      icon="edit"
      onSubmit={handleUpdate}
      onCancel={() => setShowConfirmCancel(true)}
      submitButtonLabel={translateGeneral("save")}
      cancelButtonLabel={translateGeneral("cancel")}
      disabled={isLoading}
      numColumns={6}
    >
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("name")}
          required
          value={name}
          onChange={(val: any) => setName(val)}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("description")}
          value={description}
          onChange={(val: any) => setDescription(val)}
        />
      </div>
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="select"
          label={translateForm("plant")}
          required
          value={plantId}
          options={plants}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => {
            setPlantId(val);
            setAreaId("");
            setLineId("");
          }}
        />
      </div>
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="select"
          label={translateForm("area")}
          required
          disabled={!plantId}
          value={areaId}
          options={areas}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => {
            setAreaId(val);
            setLineId("");
          }}
        />
      </div>
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="select"
          label={translateForm("line")}
          required
          disabled={!areaId}
          value={lineId}
          options={lines}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => setLineId(val)}
        />
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/sublines")}
      />
    </FormCard>
  );
};

export default EditFormSubline;
